import { useEffect, useRef, useState } from 'preact/hooks';
import { api, ApiError } from '../../api.js';
import { t } from '../../i18n/index.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { CopyField, copyText } from '../../components/CopyField.js';
import { printInviteCards, type InviteCard } from '../../components/InviteCards.js';
import { saveInviteCards, type SaveFormat } from '../../components/InviteCardsExport.js';
import { PUBLIC_ORIGIN } from '../../joinLinks.js';
import { Ball, NumberBoard, nextDrawText } from '../../bingo/ui.js';
import { AdsLibrary, AutoPanel, AutoSettings } from './AutoBingoAdmin.js';
import { BingoPlayers } from './BingoPlayers.js';
import { BingoLogs } from './BingoLogs.js';
import { BingoMusic, StreamSettings } from './BingoMusic.js';
import { AdCarousel } from './AdCarousel.js';
import { SignupsAdmin } from './SignupsAdmin.js';
import { NearCoupons } from './NearCoupons.js';
import {
  BINGO_BATCH_MAX,
  BINGO_LABEL_MAX,
  stageLabel,
  type BingoBatchView,
  type BingoCodeView,
  type BingoHostState,
} from '../../../shared/bingo.js';

type Flash = { ok: boolean; text: string } | null;
const b = () => t.admin.bingo;
const errorText = (err: unknown) => {
  const code = err instanceof ApiError ? err.code : 'network';
  return code === 'label_taken' ? b().labelTaken : t.errors[code];
};

/**
 * Areena Bingo for the host (decision 2026-09-29): the game (draws, who is close, bingos,
 * confirming winners, OBS link) and the coupon batches (codes, registration, code cards).
 */
/**
 * Also the bingo host's page (2026-10-01): `onForbidden` is called when the permission has been
 * taken away (the server refuses), so the page can close.
 */
export function BingoAdmin(props: { onForbidden?: () => void; /** The administrator (the sound files tab, 2026-10-03). */ admin?: boolean } = {}) {
  const [batches, setBatches] = useState<BingoBatchView[]>([]);
  const [host, setHost] = useState<BingoHostState | null>(null);
  const [flash, setFlash] = useState<Flash>(null);

  const load = () =>
    api<{ batches: BingoBatchView[]; host: BingoHostState }>('GET', '/api/admin/bingo')
      .then((r) => {
        setBatches(r.batches);
        setHost(r.host);
      })
      .catch((err) => setFlash({ ok: false, text: errorText(err) }));
  useEffect(() => {
    void load();
  }, []);

  // The host view follows the game (polled; the countdown to the next draw needs fresh data).
  const version = useRef<number | null>(null);
  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const since = version.current === null ? '' : `?since=${version.current}`;
        const r = await api<BingoHostState & { unchanged?: boolean }>('GET', `/api/admin/bingo/host${since}`);
        if (!r.unchanged) {
          if (version.current !== null && r.version !== version.current) void load();
          version.current = r.version;
          setHost(r);
        }
      } catch (err) {
        if (err instanceof ApiError && (err.code === 'forbidden' || err.code === 'unauthorized') && props.onForbidden) {
          props.onForbidden();
          return;
        }
        // Next round.
      }
      if (!stopped) timer = setTimeout(poll, 1000);
    };
    void poll();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, []);

  const running = host?.game?.status === 'running';
  const [sub, setSub] = useState<SubTab>(storedSub());
  const choose = (next: SubTab) => {
    setSub(next);
    try {
      localStorage.setItem(SUB_KEY, next);
    } catch {
      // Storage unavailable: the choice is not remembered.
    }
  };
  const started = (h: BingoHostState) => {
    setHost(h);
    void load();
  };
  return (
    <div class="stack">
      {flash && (
        <p class={flash.ok ? 'success' : 'error'} role="status">
          {flash.text}
        </p>
      )}
      {running && host && (host.game!.mode === 'auto' ? <AutoPanel host={host} onHost={setHost} onFlash={setFlash} /> : <HostPanel host={host} onHost={setHost} onFlash={setFlash} />)}
      {/* Automatic bingo and its ads on their own tabs (decision 2026-09-30). */}
      <div class="tabs bingo-subtabs" role="tablist">
        {SUB_TABS.filter((id) => id !== 'audio' || props.admin).map((id) => (
          <button role="tab" key={id} aria-selected={sub === id} class={`tab ${sub === id ? 'active' : ''}`} onClick={() => choose(id)}>
            {t.admin.autoBingo.tabs[id]}
          </button>
        ))}
      </div>
      {/* Batches and the hosted game on their own tabs (user request 2026-10-01). */}
      {sub === 'batches' && (
        <>
          <Batches batches={batches} onChanged={() => void load()} onFlash={setFlash} />
          <SponsorCard version={host?.sponsor ?? null} onChanged={() => void load()} onFlash={setFlash} />
        </>
      )}
      {sub === 'host' && (
        <>
          {!running && <StartGame batches={batches} last={host} onStarted={started} onChanged={() => void load()} onFlash={setFlash} />}
          <NextDrawCard onFlash={setFlash} />
          <ObsLinks />
        </>
      )}
      {sub === 'auto' && (
        <>
          <AutoSettings batches={batches} running={running ? host!.game!.mode : null} onStarted={started} onFlash={setFlash} />
          <ObsLinks />
        </>
      )}
      {sub === 'ads' && <AdCarousel onFlash={setFlash} admin={props.admin === true} />}
      {/* All the sound files and the stream address: the administrator only (user request 2026-10-03). */}
      {sub === 'audio' && props.admin && (
        <>
          <BingoMusic onFlash={setFlash} />
          <AdsLibrary kind="ad" uploadOnly onFlash={setFlash} />
          <AdsLibrary kind="filler" onFlash={setFlash} />
          <StreamSettings onFlash={setFlash} />
        </>
      )}
      {sub === 'players' && <BingoPlayers onFlash={setFlash} />}
      {sub === 'logs' && <BingoLogs onFlash={setFlash} />}
      {sub === 'sms' && <SignupsAdmin onFlash={setFlash} />}
    </div>
  );
}

const SUB_TABS = ['batches', 'players', 'host', 'auto', 'ads', 'sms', 'logs', 'audio'] as const;
type SubTab = (typeof SUB_TABS)[number];
const SUB_KEY = 'areena.bingoTab';
function storedSub(): SubTab {
  try {
    const v = localStorage.getItem(SUB_KEY);
    return (SUB_TABS as readonly string[]).includes(v ?? '') ? (v as SubTab) : 'batches';
  } catch {
    return 'host';
  }
}

/**
 * Starting a game (user request 2026-09-30): choose one or more batches (e.g. codes on paper and
 * electronic codes), close their registration, then start. Starting closes a registration that
 * is still open.
 */
function StartGame(props: {
  batches: BingoBatchView[];
  last: BingoHostState | null;
  onStarted: (h: BingoHostState) => void;
  onChanged: () => void;
  onFlash: (f: Flash) => void;
}) {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const finished = props.last?.game?.status === 'finished' ? props.last.game : null;
  const chosen = props.batches.filter((x) => selected.has(x.id));
  const players = chosen.reduce((sum, x) => sum + x.claimed, 0);
  const openOnes = chosen.filter((x) => x.registrationOpen);

  const toggle = (id: number, on: boolean) => {
    const next = new Set(selected);
    if (on) next.add(id);
    else next.delete(id);
    setSelected(next);
  };
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
      props.onFlash(null);
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    } finally {
      setBusy(false);
    }
  };
  const closing = useCloseConfirm(props.onFlash);
  const closeRegistration = () =>
    closing.ask(
      openOnes.map((x) => x.id),
      () =>
        run(async () => {
          await api('POST', '/api/admin/bingo/registration', { batchIds: openOnes.map((x) => x.id), open: false });
          props.onChanged();
        }),
    );
  // Starting closes the claiming as well.
  const start = () =>
    closing.ask(
      openOnes.map((x) => x.id),
      () =>
        run(async () => {
          props.onStarted(await api<BingoHostState>('POST', '/api/admin/bingo/game', { batchIds: [...selected] }));
          setSelected(new Set());
        }),
    );

  return (
    <section class="card form">
      <h2>{b().gameTitle}</h2>
      {finished && (
        <div class="bingo-finished">
          <p>
            <b>{b().finished}</b> {props.last?.labels.join(' + ')} · {b().drawn(finished.draws.length, finished.players)}
          </p>
          <Winners winners={finished.winners} />
        </div>
      )}
      <p class="hint">{b().startInfo}</p>
      {props.batches.length === 0 ? (
        <p class="muted">{b().none}</p>
      ) : (
        <fieldset class="card-format">
          <legend>{b().chooseBatches}</legend>
          {props.batches.map((x) => (
            <label class="switch" key={x.id}>
              <input type="checkbox" checked={selected.has(x.id)} onChange={(e) => toggle(x.id, e.currentTarget.checked)} />
              <span class="switch-track" aria-hidden="true" />
              <span>
                <b>{x.label}</b> · {b().range(x.firstNumber, x.firstNumber + x.size - 1)} · {b().claimed(x.claimed, x.size)} ·{' '}
                {x.registrationOpen ? b().registrationOpen : b().registrationClosed}
              </span>
            </label>
          ))}
        </fieldset>
      )}
      {chosen.length > 0 && <p>{b().selectedInfo(chosen.length, players)}</p>}
      <div class="bingo-start-actions">
        <button class="btn" type="button" disabled={busy || openOnes.length === 0} onClick={() => void closeRegistration()}>
          🔒 {b().closeSelected(openOnes.length)}
        </button>
        <button class="btn btn-primary" type="button" disabled={busy || chosen.length === 0 || players === 0} onClick={() => void start()}>
          ▶ {finished ? b().newGame : b().start}
        </button>
      </div>
      {openOnes.length > 0 && <p class="hint">{b().autoClose}</p>}
      {closing.dialog}
    </section>
  );
}

/**
 * Closing the coupon claiming voids the coupons and reserved codes of the absent players (user decision
 * 2026-10-02): the admin sees how many first. Nothing to void → closes at once.
 */
export function useCloseConfirm(onFlash: (f: Flash) => void) {
  const [pending, setPending] = useState<{ voids: number; run: () => void } | null>(null);
  const ask = (batchIds: number[], run: () => void) => {
    if (batchIds.length === 0) return run();
    api<{ voids: number }>('GET', `/api/admin/bingo/absent?batchIds=${batchIds.join(',')}`)
      .then((r) => (r.voids > 0 ? setPending({ voids: r.voids, run }) : run()))
      .catch((err) => onFlash({ ok: false, text: errorText(err) }));
  };
  const dialog = pending && (
    <ConfirmDialog
      title={b().voidTitle(pending.voids)}
      confirmLabel={b().voidConfirm}
      cancelLabel={t.admin.cancel}
      danger
      onConfirm={() => {
        const run = pending.run;
        setPending(null);
        run();
      }}
      onCancel={() => setPending(null)}
    >
      <p>{b().voidText}</p>
    </ConfirmDialog>
  );
  return { ask, dialog };
}

export function Winners(props: { winners: NonNullable<BingoHostState['game']>['winners'] }) {
  if (props.winners.length === 0) return null;
  return (
    <div class="bingo-winners">
      <b>{b().winners}</b>
      {props.winners.map((w) => (
        <div key={`${w.stage}-${w.cardNumber}`}>
          {stageLabel(w.stage)}: {b().cardLabel(w.cardNumber, w.nickname)}
          {w.jackpot && <b> · 💎 SUPERJACKPOT</b>}
        </div>
      ))}
    </div>
  );
}

function HostPanel(props: { host: BingoHostState; onHost: (h: BingoHostState) => void; onFlash: (f: Flash) => void }) {
  const game = props.host.game!;
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [confirmEnd, setConfirmEnd] = useState(false);
  // Local countdown between polls.
  const [now, setNow] = useState(Date.now());
  const readyAt = useRef(0);
  useEffect(() => {
    readyAt.current = Date.now() + props.host.nextDrawInMs;
  }, [props.host.nextDrawInMs, props.host.version]);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(timer);
  }, []);
  // Bingo! presses of the players (2026-10-01): selected by default; without presses all bingos are.
  const calls = game.calls;
  const called = new Set(calls.map((c) => c.cardNumber));
  useEffect(() => {
    const list = props.host.bingo.map((x) => x.cardNumber);
    setSelected(new Set(called.size > 0 ? list.filter((n) => called.has(n)) : list));
  }, [props.host.bingo.map((x) => x.cardNumber).join(','), calls.map((c) => c.cardNumber).join(',')]);
  // A new press: a short chime for the host.
  const heard = useRef(calls.length);
  useEffect(() => {
    if (calls.length > heard.current) chime();
    heard.current = calls.length;
  }, [calls.length]);

  const act = async (path: string, body?: unknown) => {
    setBusy(true);
    try {
      props.onHost(await api<BingoHostState>('POST', path, body));
      props.onFlash(null);
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    } finally {
      setBusy(false);
    }
  };
  const wait = Math.ceil(Math.max(0, readyAt.current - now) / 1000);
  const last = game.draws[game.draws.length - 1];

  return (
    <section class="card bingo-host">
      <div class="bingo-host-head">
        <div>
          <h2>
            {b().gameTitle}: {props.host.labels.join(' + ')}
          </h2>
          <div class="bingo-host-stage">{b().stage(stageLabel(game.stage))}</div>
          <div class="muted">{b().drawn(game.draws.length, game.players)}</div>
        </div>
        <div class="bingo-host-last">
          {last !== undefined ? <Ball n={last} size={120} key={last} /> : <span class="muted">{b().noDraws}</span>}
        </div>
      </div>
      <button class="btn btn-primary bingo-next" disabled={busy || wait > 0 || game.draws.length >= 75} onClick={() => void act('/api/admin/bingo/game/draw')}>
        {wait > 0 ? b().wait(wait) : `🎱 ${b().next}`}
      </button>

      {calls.length > 0 && (
        <div class="bingo-calls" role="alert">
          <h3>📣 {b().callsTitle(calls.length)}</h3>
          {calls.map((c) => (
            <div key={c.cardNumber}>
              <b>{b().cardLabel(c.cardNumber, c.nickname)}</b> · {new Date(c.at).toLocaleTimeString('fi-FI')}
            </div>
          ))}
          <p class="hint">{b().callsHint}</p>
        </div>
      )}
      <div class="bingo-host-grid">
        <div class={`bingo-panel ${props.host.bingo.length > 0 ? 'has-bingo' : ''}`}>
          <h3>{b().bingoTitle}</h3>
          {props.host.bingo.length === 0 ? (
            <p class="muted">{b().bingoNone}</p>
          ) : (
            <>
              {props.host.bingo.map((x) => (
                <label class="switch" key={x.cardNumber}>
                  <input
                    type="checkbox"
                    checked={selected.has(x.cardNumber)}
                    onChange={(e) => {
                      const next = new Set(selected);
                      if (e.currentTarget.checked) next.add(x.cardNumber);
                      else next.delete(x.cardNumber);
                      setSelected(next);
                    }}
                  />
                  <span class="switch-track" aria-hidden="true" />
                  <b>{b().cardLabel(x.cardNumber, x.nickname)}</b>
                  {called.has(x.cardNumber) && <span class="badge badge-call">📣 {b().calledBadge}</span>}
                </label>
              ))}
              <button
                class="btn btn-success"
                disabled={busy || selected.size === 0}
                onClick={() => void act('/api/admin/bingo/game/confirm', { cardNumbers: [...selected] })}
              >
                ✔ {b().confirm(selected.size)}
              </button>
              <p class="hint">{b().confirmHint}</p>
            </>
          )}
        </div>
      </div>
      <NearCoupons host={props.host} />

      <NumberBoard draws={game.draws} class="bingo-host-board" />
      <Winners winners={game.winners} />
      <div>
        <button class="btn btn-danger btn-small" disabled={busy} onClick={() => setConfirmEnd(true)}>
          {b().end}
        </button>
      </div>
      {confirmEnd && (
        <EndGameDialog
          onEnd={() => {
            setConfirmEnd(false);
            void act('/api/admin/bingo/game/end');
          }}
          onCancel={() => setConfirmEnd(false)}
        />
      )}
    </section>
  );
}

/** Ending the game needs the word LOPETA, so that it cannot happen by accident (user request 2026-09-30). */
export function EndGameDialog(props: { onEnd: () => void; onCancel: () => void }) {
  const [word, setWord] = useState('');
  const ok = word.trim().toUpperCase() === b().endWord;
  return (
    <ConfirmDialog
      title={b().endTitle}
      confirmLabel={b().end}
      cancelLabel={t.admin.cancel}
      danger
      confirmDisabled={!ok}
      onConfirm={() => ok && props.onEnd()}
      onCancel={props.onCancel}
    >
      <p>{b().endText}</p>
      <label class="form">
        {b().endType}
        <input value={word} onInput={(e) => setWord(e.currentTarget.value)} autocomplete="off" autocapitalize="characters" spellcheck={false} />
      </label>
    </ConfirmDialog>
  );
}

function ObsLinks() {
  return (
    <section class="card form">
      <h2>{b().obsTitle}</h2>
      <p class="hint">{b().obsInfo}</p>
      {(['tumma', 'lapinakyva', 'vihrea'] as const).map((bg) => (
        <CopyField key={bg} value={`${PUBLIC_ORIGIN}/bingo/lahetys/?tausta=${bg}`} label={b().obsLink(b().backgrounds[bg] ?? bg)} />
      ))}
      {(['tumma', 'lapinakyva', 'vihrea'] as const).map((bg) => (
        <CopyField key={`near-${bg}`} value={`${PUBLIC_ORIGIN}/bingo/lahetys/?nakyma=kupongit&tausta=${bg}`} label={b().obsNearLink(b().backgrounds[bg] ?? bg)} />
      ))}
      <CopyField value={`${PUBLIC_ORIGIN}/bingo/`} label={b().publicLink} />
    </section>
  );
}

/** "datetime-local" value in local time. */
const toLocalInput = (ms: number) => {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

/** The next draw, announced in the lobby and on the bingo page (user request 2026-09-30). */
function NextDrawCard(props: { onFlash: (f: Flash) => void }) {
  const [when, setWhen] = useState('');
  const [text, setText] = useState('');
  const [saved, setSaved] = useState<{ at: number; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api<{ next: { at: number; text: string } | null }>('GET', '/api/admin/bingo/next')
      .then((r) => {
        setSaved(r.next);
        if (r.next) {
          setWhen(toLocalInput(r.next.at));
          setText(r.next.text);
        }
      })
      .catch(() => {});
  }, []);
  const save = async (at: number | null) => {
    setBusy(true);
    try {
      const r = await api<{ next: { at: number; text: string } | null }>('PUT', '/api/admin/bingo/next', { at, text: text.trim() });
      setSaved(r.next);
      if (!r.next) {
        setWhen('');
        setText('');
      }
      props.onFlash({ ok: true, text: r.next ? b().nextSaved : b().nextRemoved });
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    } finally {
      setBusy(false);
    }
  };
  const submit = (e: Event) => {
    e.preventDefault();
    const at = new Date(when).getTime();
    if (!when || Number.isNaN(at)) return;
    void save(at);
  };
  return (
    <form class="card form" onSubmit={submit}>
      <h2>{b().nextTitle}</h2>
      <p class="hint">{b().nextInfo}</p>
      <div class="grid-2">
        <label>
          {b().nextWhen}
          <input type="datetime-local" value={when} onInput={(e) => setWhen(e.currentTarget.value)} required />
        </label>
        <label>
          {b().nextText}
          <input value={text} maxLength={160} onInput={(e) => setText(e.currentTarget.value)} placeholder={b().nextTextPlaceholder} />
        </label>
      </div>
      {saved && <p class="success">{b().nextShown(nextDrawText(saved.at))}</p>}
      <div class="bingo-start-actions">
        <button class="btn btn-primary" type="submit" disabled={busy}>
          {b().nextSave}
        </button>
        {saved && (
          <button class="btn btn-danger" type="button" disabled={busy} onClick={() => void save(null)}>
            {b().nextRemove}
          </button>
        )}
      </div>
    </form>
  );
}

/** Sponsor logo in the free square of every coupon (user request 2026-09-29). */
function SponsorCard(props: { version: number | null; onChanged: () => void; onFlash: (f: Flash) => void }) {
  const [busy, setBusy] = useState(false);
  const pick = (e: Event) => {
    const input = e.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) return props.onFlash({ ok: false, text: b().sponsorType });
    if (file.size > 300 * 1024) return props.onFlash({ ok: false, text: b().sponsorTooBig });
    const reader = new FileReader();
    reader.onload = async () => {
      setBusy(true);
      try {
        await api('PUT', '/api/admin/bingo/sponsor', { dataUrl: String(reader.result) });
        props.onFlash({ ok: true, text: b().sponsorSaved });
        props.onChanged();
      } catch (err) {
        props.onFlash({ ok: false, text: errorText(err) });
      } finally {
        setBusy(false);
      }
    };
    reader.readAsDataURL(file);
  };
  const remove = async () => {
    setBusy(true);
    try {
      await api('DELETE', '/api/admin/bingo/sponsor');
      props.onFlash({ ok: true, text: b().sponsorRemoved });
      props.onChanged();
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <section class="card form">
      <h2>{b().sponsorTitle}</h2>
      <p class="hint">{b().sponsorInfo}</p>
      <div class="bingo-sponsor-row">
        <div class="coupon-cell free bingo-sponsor-preview">
          {props.version ? <img src={`/api/bingo/sponsor?v=${props.version}`} alt="" /> : <>★<small>VAPAA</small></>}
        </div>
        <label class={`btn btn-small ${busy ? 'is-disabled' : ''}`}>
          {b().sponsorPick}
          <input type="file" accept="image/png,image/jpeg,image/webp" onChange={pick} disabled={busy} hidden />
        </label>
        {props.version && (
          <button class="btn btn-small btn-danger" disabled={busy} onClick={() => void remove()}>
            {b().sponsorRemove}
          </button>
        )}
      </div>
    </section>
  );
}

function Batches(props:{ batches: BingoBatchView[]; onChanged: () => void; onFlash: (f: Flash) => void }) {
  const [label, setLabel] = useState('');
  const [size, setSize] = useState(20);
  const [busy, setBusy] = useState(false);
  const create = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api('POST', '/api/admin/bingo/batches', { label: label.trim(), size });
      props.onFlash({ ok: true, text: b().created(label.trim(), size) });
      setLabel('');
      props.onChanged();
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <form class="card form" onSubmit={create}>
        <h2>{b().newBatch}</h2>
        <p class="hint">{b().newBatchInfo}</p>
        <div class="grid-2">
          <label>
            {b().label}
            <input value={label} onInput={(e) => setLabel(e.currentTarget.value)} maxLength={BINGO_LABEL_MAX} required />
          </label>
          <label>
            {b().size}
            <input type="number" min={1} max={BINGO_BATCH_MAX} value={size} onInput={(e) => setSize(Number(e.currentTarget.value))} required />
          </label>
        </div>
        <button class="btn btn-primary" type="submit" disabled={busy}>
          {b().create}
        </button>
      </form>
      <section class="card">
        <h2>{b().batchesTitle}</h2>
        {props.batches.length === 0 ? (
          <p class="muted">{b().none}</p>
        ) : (
          <ul class="user-list">
            {props.batches.map((x) => (
              <BatchRow key={x.id} batch={x} onChanged={props.onChanged} onFlash={props.onFlash} />
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

function BatchRow(props: { batch: BingoBatchView; onChanged: () => void; onFlash: (f: Flash) => void }) {
  const x = props.batch;
  const [codes, setCodes] = useState<BingoCodeView[] | null>(null);
  const [open, setOpen] = useState(false);
  const [dialog, setDialog] = useState<'print' | 'save' | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const loadCodes = async () => {
    try {
      const r = await api<{ codes: BingoCodeView[] }>('GET', `/api/admin/bingo/batches/${x.id}`);
      setCodes(r.codes);
      return r.codes;
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
      return null;
    }
  };
  const toggle = async () => {
    if (!open) await loadCodes();
    setOpen(!open);
  };
  // Codes sent by text message or reserved for a player are not free any more (2026-10-01, 2026-10-02).
  const free = (list: BingoCodeView[]) => list.filter((c) => c.cardNumber === null && !c.sms && !c.reserved);
  const copyFree = async () => {
    const list = free((await loadCodes()) ?? []);
    if (list.length === 0) return props.onFlash({ ok: false, text: b().noFree });
    const ok = await copyText(list.map((c) => c.code).join('\n'));
    props.onFlash(ok ? { ok: true, text: b().copied(list.length) } : { ok: false, text: t.admin.copyFailed });
  };
  const setRegistration = async (value: boolean) => {
    try {
      await api('PATCH', `/api/admin/bingo/batches/${x.id}`, { registrationOpen: value });
      props.onChanged();
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    }
  };
  const closing = useCloseConfirm(props.onFlash);
  const remove = async () => {
    setConfirmRemove(false);
    try {
      await api('DELETE', `/api/admin/bingo/batches/${x.id}`);
      props.onChanged();
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    }
  };

  return (
    <li class="user-row batch-row">
      <div class="user-main">
        <div class="user-name">
          {x.label} {x.inGame && <span class="badge batch-active">{b().inGame}</span>}
        </div>
        <div class="user-sub">
          {b().range(x.firstNumber, x.firstNumber + x.size - 1)} · {b().claimed(x.claimed, x.size)} ·{' '}
          {x.registrationOpen ? b().registrationOpen : b().registrationClosed}
        </div>
      </div>
      <div class="user-actions">
        <button class="btn btn-small" onClick={() => void toggle()} aria-expanded={open}>
          {open ? b().hideCodes : b().showCodes}
        </button>
        <button class="btn btn-small" onClick={() => void copyFree()}>
          {b().copyFree}
        </button>
        <button class="btn btn-small" onClick={() => void loadCodes().then((c) => c && setDialog('print'))}>
          🖨 {b().print}
        </button>
        <button class="btn btn-small" onClick={() => void loadCodes().then((c) => c && setDialog('save'))}>
          💾 {b().save}
        </button>
        {!x.inGame && (
          <button
            class="btn btn-small"
            onClick={() => (x.registrationOpen ? closing.ask([x.id], () => setRegistration(false)) : void setRegistration(true))}
          >
            {x.registrationOpen ? b().closeRegistration : b().openRegistration}
          </button>
        )}
        {!x.inGame && (
          <button class="btn btn-small btn-danger" onClick={() => setConfirmRemove(true)}>
            {b().remove}
          </button>
        )}
      </div>
      {open && codes && (
        <ul class="code-list">
          {codes.map((c) => (
            <li key={c.code} class={c.cardNumber ? 'used' : ''}>
              <code>{c.code}</code>
              <span>
                {c.cardNumber ? b().cardLabel(c.cardNumber, c.nickname ?? '') : c.reserved ? b().reservedFor(c.nickname ?? '') : c.sms ? b().smsSent : b().free}
              </span>
            </li>
          ))}
        </ul>
      )}
      {closing.dialog}
      {dialog && codes && <CodeCardsDialog mode={dialog} batch={x} codes={free(codes)} onClose={() => setDialog(null)} />}
      {confirmRemove && (
        <ConfirmDialog
          title={b().removeTitle(x.label)}
          confirmLabel={b().remove}
          cancelLabel={t.admin.cancel}
          danger
          onConfirm={() => void remove()}
          onCancel={() => setConfirmRemove(false)}
        >
          <p>{b().removeText}</p>
        </ConfirmDialog>
      )}
    </li>
  );
}

/** Code cards of the free codes: print (8 per A4) or save as PDF/JPG – like the invite cards. */
function CodeCardsDialog(props: { mode: 'print' | 'save'; batch: BingoBatchView; codes: BingoCodeView[]; onClose: () => void }) {
  const [withQr, setWithQr] = useState(true);
  const [format, setFormat] = useState<SaveFormat>('pdf');
  const [busy, setBusy] = useState(false);
  const cards: InviteCard[] = props.codes.map((c) => ({ code: c.code, expiresAt: 0, kind: 'bingo', note: props.batch.label }));
  const i = t.admin.invites;
  const go = async () => {
    if (cards.length === 0 || busy) return;
    if (props.mode === 'print') return printInviteCards(cards, withQr);
    setBusy(true);
    try {
      await saveInviteCards(cards, withQr, format, props.batch.label);
    } finally {
      setBusy(false);
    }
  };
  return (
    <ConfirmDialog
      title={`${b().cardTitle} – ${props.batch.label}`}
      confirmLabel={props.mode === 'print' ? `🖨 ${i.printNow}` : `💾 ${i.saveNow}`}
      cancelLabel={t.admin.close}
      onConfirm={() => void go()}
      onCancel={props.onClose}
    >
      <div class="form">
        {props.mode === 'save' && (
          <fieldset class="card-format">
            <legend>{i.format}</legend>
            {(['pdf', 'jpg-sheets', 'jpg-cards', 'txt-zip'] as const).map((f) => (
              <label class="radio" key={f}>
                <input type="radio" name="bingo-format" checked={format === f} onChange={() => setFormat(f)} />
                {f === 'pdf' ? i.formatPdf : f === 'jpg-sheets' ? i.formatJpgSheets : f === 'jpg-cards' ? i.formatJpgCards : b().formatTxt}
              </label>
            ))}
          </fieldset>
        )}
        {/* Text files have a direct link instead of a QR code. */}
        {!(props.mode === 'save' && format === 'txt-zip') && (
          <label class="switch">
            <input type="checkbox" checked={withQr} onChange={(e) => setWithQr(e.currentTarget.checked)} />
            <span class="switch-track" aria-hidden="true" />
            {b().qr}
          </label>
        )}
        <p>{cards.length > 0 ? i.printCount(cards.length) : b().noFree}</p>
      </div>
    </ConfirmDialog>
  );
}

/** A short two-tone chime when a player presses Bingo! (the host has used the page, so sound may play). */
function chime() {
  try {
    const ctx = new AudioContext();
    [880, 1320].forEach((freq, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      const t0 = ctx.currentTime + i * 0.16;
      o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.25, t0 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.3);
      o.connect(g).connect(ctx.destination);
      o.start(t0);
      o.stop(t0 + 0.32);
    });
    setTimeout(() => void ctx.close(), 1000);
  } catch {
    // No sound: the panel still shows the call.
  }
}
